import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, Gauge, Weight, Calendar, X, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import AlertButton from "@/components/AlertButton";
import { ExpertCard } from "@/components/ExpertCard";
import ProductCardSkeleton from "@/components/ProductCardSkeleton";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";

const POWER_RANGES = {
  "0-5": { max_power: 5 },
  "5-15": { min_power: 5, max_power: 15 },
  "15-30": { min_power: 15, max_power: 30 },
  "30+": { min_power: 30 },
};
const WEIGHT_RANGES = {
  "0-500": { max_weight: 500 },
  "500-2000": { min_weight: 500, max_weight: 2000 },
  "2000-6000": { min_weight: 2000, max_weight: 6000 },
  "6000+": { min_weight: 6000 },
};
const YEAR_RANGES = {
  "2024": { min_year: 2024 },
  "2022-2023": { min_year: 2022, max_year: 2023 },
  "old": { max_year: 2021 },
};

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [experts, setExperts] = useState([]);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const cat = params.get("cat") || "";
  const sort = params.get("sort") || "";
  const power = params.get("power") || "";
  const weight = params.get("weight") || "";
  const year = params.get("year") || "";
  const page = parseInt(params.get("page") || "1", 10);
  const PAGE_SIZE = 24;
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  useEffect(() => {
    api.get("/categories").then((r) => setTree(r.data)).catch(() => {});
  }, []);

  const catName = (() => {
    const find = (nodes) => {
      for (const n of nodes) {
        if (n.slug === cat) return n.name;
        if (n.children) { const r = find(n.children); if (r) return r; }
      }
      return "";
    };
    return find(tree);
  })();

  const load = useCallback(() => {
    setLoading(true);
    const q = {};
    if (cat) q.category = cat;
    if (sort) q.sort = sort;
    if (search) q.search = search;
    if (power && POWER_RANGES[power]) Object.assign(q, POWER_RANGES[power]);
    if (weight && WEIGHT_RANGES[weight]) Object.assign(q, WEIGHT_RANGES[weight]);
    if (year && YEAR_RANGES[year]) Object.assign(q, YEAR_RANGES[year]);
    q.page = page;
    q.page_size = PAGE_SIZE;
    api.get("/products", { params: q }).then((r) => {
      const d = r.data;
      setProducts(d.items || []);
      setTotal(d.total ?? (d.items || []).length);
      setPages(d.pages || 1);
    }).finally(() => setLoading(false));
    if (search) api.get("/experts", { params: { search } }).then((r) => setExperts(r.data)).catch(() => setExperts([]));
    else setExperts([]);
  }, [cat, sort, search, power, weight, year, page]);

  // Reset to first page whenever the query/filters change (keep deep-linked page on first load for SEO).
  const filtersMounted = useRef(false);
  useEffect(() => {
    if (!filtersMounted.current) { filtersMounted.current = true; return; }
    if (params.get("page")) {
      const p = new URLSearchParams(params);
      p.delete("page");
      setParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, sort, search, power, weight, year]);

  const goToPage = (n) => {
    const p = new URLSearchParams(params);
    if (n <= 1) p.delete("page"); else p.set("page", String(n));
    setParams(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const hasActiveFilters = Boolean(cat || sort || power || weight || year || search);

  const SORT_LABELS = { price_asc: "Preț crescător", price_desc: "Preț descrescător", name: "Nume A-Z" };
  const POWER_LABELS = { "0-5": "Sub 5 kW", "5-15": "5–15 kW", "15-30": "15–30 kW", "30+": "Peste 30 kW" };
  const WEIGHT_LABELS = { "0-500": "Sub 500 kg", "500-2000": "500 kg–2 t", "2000-6000": "2–6 t", "6000+": "Peste 6 t" };
  const YEAR_LABELS = { "2024": "2024 (nou)", "2022-2023": "2022–2023", "old": "Înainte de 2022" };

  const hrefWithout = (key) => {
    const p = new URLSearchParams(params);
    p.delete(key);
    const s = p.toString();
    return s ? `/produse?${s}` : "/produse";
  };

  const chips = [];
  if (cat) chips.push({ key: "cat", label: catName || cat });
  if (power) chips.push({ key: "power", label: POWER_LABELS[power] || power });
  if (weight) chips.push({ key: "weight", label: WEIGHT_LABELS[weight] || weight });
  if (year) chips.push({ key: "year", label: YEAR_LABELS[year] || year });
  if (sort) chips.push({ key: "sort", label: SORT_LABELS[sort] || sort });

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Catalog</span>
        <h1 className="text-4xl font-black tracking-tight text-slate-900 mt-2">{catName || "Produse"}</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row lg:flex-wrap gap-4 mb-8 pb-8 border-b border-slate-200">
        <div className="relative flex-1 lg:min-w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută produse, mărci..."
            className="pl-10 rounded-none border-slate-300 h-12" data-testid="search-input" />
        </div>
        <Select value={cat || "all"} onValueChange={(v) => setParam("cat", v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-none border-slate-300 h-12 lg:w-72" data-testid="category-filter">
            <SlidersHorizontal className="w-4 h-4 mr-2 shrink-0" />
            <SelectValue placeholder="Categorie" />
          </SelectTrigger>
          <SelectContent className="max-h-[60vh]">
            <SelectItem value="all">Toate categoriile</SelectItem>
            {tree.map((t) => (
              <SelectGroup key={t.slug}>
                <SelectLabel className="text-orange-600 pt-3">{t.name}</SelectLabel>
                <SelectItem value={t.slug}>
                  <span className="inline-flex items-center gap-2">Toate · {t.name.replace(/^\d+ · /, "")}
                    {t.count > 0 && <span className="text-[10px] font-mono text-slate-400">({t.count})</span>}</span>
                </SelectItem>
                {(t.children || []).map((c) => (
                  <div key={c.slug}>
                    <SelectItem value={c.slug} className="pl-6 font-medium text-slate-700">
                      <span className="inline-flex items-center gap-2">{c.name}
                        {c.count > 0 && <span className="text-[10px] font-mono text-slate-400">({c.count})</span>}</span>
                    </SelectItem>
                    {(c.children || []).map((leaf) => (
                      <SelectItem key={leaf.slug} value={leaf.slug} className="pl-12 text-slate-500 text-xs">
                        <span className="inline-flex items-center gap-2">{leaf.name}
                          {leaf.count > 0 && <span className="text-[10px] font-mono text-orange-500">({leaf.count})</span>}</span>
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort || "default"} onValueChange={(v) => setParam("sort", v === "default" ? "" : v)}>
          <SelectTrigger className="rounded-none border-slate-300 h-12 lg:w-48" data-testid="sort-filter">
            <SelectValue placeholder="Sortează" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Implicit</SelectItem>
            <SelectItem value="price_asc">Preț crescător</SelectItem>
            <SelectItem value="price_desc">Preț descrescător</SelectItem>
            <SelectItem value="name">Nume A-Z</SelectItem>
          </SelectContent>
        </Select>
        <Select value={power || "all"} onValueChange={(v) => setParam("power", v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-none border-slate-300 h-12 lg:w-48" data-testid="power-filter">
            <Gauge className="w-4 h-4 mr-2 shrink-0" />
            <SelectValue placeholder="Putere motor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Orice putere</SelectItem>
            <SelectItem value="0-5">Sub 5 kW</SelectItem>
            <SelectItem value="5-15">5 – 15 kW</SelectItem>
            <SelectItem value="15-30">15 – 30 kW</SelectItem>
            <SelectItem value="30+">Peste 30 kW</SelectItem>
          </SelectContent>
        </Select>
        <Select value={weight || "all"} onValueChange={(v) => setParam("weight", v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-none border-slate-300 h-12 lg:w-48" data-testid="weight-filter">
            <Weight className="w-4 h-4 mr-2 shrink-0" />
            <SelectValue placeholder="Greutate" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Orice greutate</SelectItem>
            <SelectItem value="0-500">Sub 500 kg</SelectItem>
            <SelectItem value="500-2000">500 kg – 2 t</SelectItem>
            <SelectItem value="2000-6000">2 – 6 t</SelectItem>
            <SelectItem value="6000+">Peste 6 t</SelectItem>
          </SelectContent>
        </Select>
        <Select value={year || "all"} onValueChange={(v) => setParam("year", v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-none border-slate-300 h-12 lg:w-44" data-testid="year-filter">
            <Calendar className="w-4 h-4 mr-2 shrink-0" />
            <SelectValue placeholder="An fabricație" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Orice an</SelectItem>
            <SelectItem value="2024">2024 (nou)</SelectItem>
            <SelectItem value="2022-2023">2022 – 2023</SelectItem>
            <SelectItem value="old">Înainte de 2022</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <a href="/produse" data-testid="reset-filters"
            className="inline-flex items-center justify-center gap-2 h-12 px-5 border border-slate-300 text-slate-700 font-semibold text-sm uppercase tracking-wide hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors no-underline">
            <X className="w-4 h-4" /> Resetează filtrele
          </a>
        )}
        <AlertButton
          variant="outline"
          criteria={{ keyword: search || "", category: cat || "", condition: "" }}
          testid="catalog-alert-subscribe"
        />
      </div>

      {(chips.length > 0 || search) && (
        <div className="sticky top-[112px] z-30 bg-white/95 backdrop-blur-sm -mx-6 px-6 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2 mb-8" data-testid="active-filters">
          <span className="text-xs uppercase tracking-wide font-bold text-slate-400 mr-1">Filtre active:</span>
          {search && (
            <button onClick={() => setSearch("")} data-testid="chip-search"
              className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium pl-3 pr-2 py-1.5 transition-colors">
              „{search}" <X className="w-3.5 h-3.5" />
            </button>
          )}
          {chips.map((c) => (
            <a key={c.key} href={hrefWithout(c.key)} data-testid={`chip-${c.key}`}
              className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium pl-3 pr-2 py-1.5 transition-colors no-underline max-w-xs">
              <span className="truncate">{c.label}</span> <X className="w-3.5 h-3.5 shrink-0" />
            </a>
          ))}
        </div>
      )}

      {search && experts.length > 0 && (
        <div className="mb-10" data-testid="catalog-experts">
          <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-orange-600 mb-4">Experți pentru „{search}" ({experts.length})</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {experts.map((e) => <ExpertCard key={e.id} expert={e} />)}
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="product-grid-skeleton">
          {Array.from({ length: 8 }).map((_, i) => <ProductCardSkeleton key={i} />)}
        </div>
      ) : products.length === 0 && experts.length === 0 ? (
        <div className="py-20 text-center" data-testid="no-products">
          <p className="text-lg font-semibold text-slate-700">
            {search ? <>Niciun rezultat pentru „{search}".</> : "Niciun produs găsit în această categorie."}
          </p>
          <p className="text-sm text-slate-400 mt-2">Încearcă alt termen sau răsfoiește categoriile principale.</p>
          <div className="flex flex-wrap justify-center gap-2 mt-6 max-w-2xl mx-auto">
            {tree.filter((t) => t.count > 0).slice(0, 8).map((t) => (
              <a key={t.slug} href={`/produse?cat=${t.slug}`}
                className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700 border border-slate-300 hover:border-orange-500 hover:text-orange-600 px-4 py-2 transition-colors no-underline">
                {t.name.replace(/^\d+ · /, "")} <span className="text-slate-400">({t.count})</span>
              </a>
            ))}
          </div>
          <a href="/produse" className="inline-block mt-8 text-sm font-bold uppercase tracking-wide text-orange-600 hover:underline">
            Vezi toate produsele
          </a>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-6" data-testid="results-count">
            {total} produse{pages > 1 ? ` · pagina ${page}/${pages}` : ""}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-12 flex-wrap" data-testid="pagination">
              <button onClick={() => goToPage(page - 1)} disabled={page <= 1}
                className="inline-flex items-center gap-1 h-10 px-3 border border-slate-300 text-sm font-semibold hover:border-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="page-prev">
                <ChevronLeft className="w-4 h-4" /> Înapoi
              </button>
              {Array.from({ length: pages }, (_, i) => i + 1)
                .filter((n) => n === 1 || n === pages || Math.abs(n - page) <= 2)
                .reduce((acc, n) => {
                  if (acc.length && n - acc[acc.length - 1] > 1) acc.push("…");
                  acc.push(n);
                  return acc;
                }, [])
                .map((n, i) => n === "…" ? (
                  <span key={`gap-${i}`} className="px-2 text-slate-400">…</span>
                ) : (
                  <button key={n} onClick={() => goToPage(n)}
                    className={`h-10 min-w-[40px] px-3 border text-sm font-bold transition-colors ${
                      n === page ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700 hover:border-slate-900"
                    }`}
                    data-testid={`page-${n}`} aria-current={n === page ? "page" : undefined}>
                    {n}
                  </button>
                ))}
              <button onClick={() => goToPage(page + 1)} disabled={page >= pages}
                className="inline-flex items-center gap-1 h-10 px-3 border border-slate-300 text-sm font-semibold hover:border-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="page-next">
                Înainte <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
